'use client';
import { generateTheme } from '@app/lib/helpers/generate-theme';
import proxyImageUrlIfEnabled from '@app/lib/images/proxy-image-url-if-enabled';
import {
    getNostoContentPlacement,
    recordNostoContentClick,
} from '@app/lib/nosto/content-attribution';
import { useScript } from '@app/scripts';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { PatternPosterData, StaticImage } from 'mmds';
import { useParams } from 'next/navigation';
import { useLayoutEffect, useState } from 'react';
import s from './NostoDistruptorPoster.module.css';

/**
 * Props for `NostoPosterPlacement`.
 */
export type NostoPosterPlacementProps =
    SliceComponentProps<Content.NostoPosterPlacementSlice>;

/**
 * Component for "NostoPosterPlacement" Slices.
 */
const NostoPosterPlacement = ({
    slice,
    index,
}: NostoPosterPlacementProps): JSX.Element | null => {
    const { lang, collection } = useParams();
    const { nostoInitialised } = useScript();
    const [posterContent, setPosterContent] = useState<any>(false);
    const [nostoResultId, setNostoResultId] = useState<string | null>(null);
    const placementIdentifier = slice?.primary.placement_id as string;

    useLayoutEffect(() => {
        let cancelled = false;

        if (!nostoInitialised || !(window as any).nostojs) {
            return () => {
                cancelled = true;
            };
        }

        try {
            (window as any)?.nostojs((api) => {
                const session = api.defaultSession();
                session
                    .viewOther(session)
                    .setPlacements([placementIdentifier])
                    .load()
                    .then((data) => {
                        const content = getNostoContentPlacement(
                            data,
                            placementIdentifier
                        );
                        if (!content?.html) return;
                        const json = JSON.parse(content.html);
                        if (!json || cancelled) return;
                        setPosterContent(json);
                        setNostoResultId(content.result_id ?? null);
                    })
                    .catch((e) => {
                        if (process.env.NODE_ENV !== 'production') {
                            console.log('Error fetching Nosto content', e);
                        }
                    });
            });
        } catch (e) {
            if (process.env.NODE_ENV !== 'production') {
                console.log('Error fetching or parsing Nosto content', e);
            }
        }

        return () => {
            cancelled = true;
        };
    }, [nostoInitialised, collection, lang, placementIdentifier]);

    if (!posterContent) return null;

    const {
        title,
        content,
        mobileTheme,
        desktopTheme,
        desktopImage,
        mobileImage,
        backgroundColor,
        cta,
        ctaLink,
    } = posterContent;

    const theme = generateTheme({
        mobileTheme,
        desktopTheme,
    });

    const recordCtaAttribution = () => {
        recordNostoContentClick(nostoResultId);
    };

    return (
        <div data-theme={theme} className={s.root} key={index}>
            <div
                className={s.posterFeatureBG}
                style={{
                    backgroundColor: backgroundColor || '#E7E7E7',
                }}
            >
                <StaticImage
                    desktopImage={desktopImage}
                    mobileImage={mobileImage}
                    altDescription={title || 'Nosto Poster'}
                    fullWidth={true}
                    urlTransformer={proxyImageUrlIfEnabled}
                />
                <PatternPosterData
                    variant="feature"
                    title={title}
                    content={content}
                    ctaSet={[
                        {
                            label: cta,
                            hrefLink: ctaLink,
                            onClick: recordCtaAttribution,
                        },
                    ]}
                    lang={lang as string}
                />
            </div>
        </div>
    );
};

export default NostoPosterPlacement;
